import React, { useEffect, useState } from 'react';
import { Package, Building2, Cloud, Database, TrendingUp, Award } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui-ios';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min

function fmt0(n) {
  return new Intl.NumberFormat('it-IT').format(Number(n || 0));
}
function fmtBytes(b) {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function Stat({ label, value, sub, icon: Icon, testid, tone = '' }) {
  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] text-[color:var(--text-secondary)] uppercase tracking-wide font-medium">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-[color:var(--text-secondary)]">{sub}</div>}
    </div>
  );
}

export default function AppStatsCard() {
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data: r } = await api.get('/admin/app-stats');
        if (alive) { setData(r); setUpdatedAt(new Date()); }
      } catch (e) { /* silent */ }
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const r2Pct = data?.r2?.configured ? Math.min(100, (data.r2.bytes / (10 * 1024 * 1024 * 1024)) * 100) : 0;

  return (
    <Card className="p-4 sm:p-5" data-testid="sa-app-stats-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Dati applicazione</h2>
          <p className="text-xs text-[color:var(--text-secondary)]">
            Statistiche reali su raccomandate, agenzie e storage.
          </p>
        </div>
        <div className="text-[11px] text-[color:var(--text-secondary)] text-right">
          {updatedAt ? `Aggiornato ${updatedAt.toLocaleTimeString('it-IT')}` : 'Caricamento…'}
        </div>
      </div>

      {/* Raccomandate */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-5">
        <Stat
          icon={Package}
          label="Oggi"
          value={data ? fmt0(data.raccomandate.today) : '—'}
          sub="raccomandate"
          testid="app-stat-today"
        />
        <Stat
          icon={TrendingUp}
          label="Settimana"
          value={data ? fmt0(data.raccomandate.week) : '—'}
          sub="ultimi 7 giorni"
          testid="app-stat-week"
        />
        <Stat
          icon={TrendingUp}
          label="Mese"
          value={data ? fmt0(data.raccomandate.month) : '—'}
          sub="ultimi 30 giorni"
          testid="app-stat-month"
        />
        <Stat
          icon={Database}
          label="Totale"
          value={data ? fmt0(data.raccomandate.total) : '—'}
          sub="storico"
          testid="app-stat-total"
        />
      </div>

      {/* Agenzie + R2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-4 border-t border-[color:var(--hairline)]">
        <div>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-[color:var(--text-secondary)] font-medium mb-2">
            <Building2 className="h-3.5 w-3.5" /> Agenzie
          </div>
          <div className="text-2xl font-bold tabular-nums" data-testid="app-stat-active-agencies">
            {data ? `${data.agencies.active_today} / ${data.agencies.total}` : '—'}
          </div>
          <div className="text-[11px] text-[color:var(--text-secondary)] mb-2">attive oggi / totale</div>
          {data?.agencies?.top_today?.length > 0 && (
            <div className="mt-2 space-y-1">
              <div className="text-[10px] uppercase tracking-wide text-[color:var(--text-secondary)] font-medium flex items-center gap-1">
                <Award className="h-3 w-3" /> Top 5 oggi
              </div>
              {data.agencies.top_today.map(a => (
                <div key={a.user_id} className="flex justify-between text-xs">
                  <span className="truncate pr-2">{a.name}</span>
                  <span className="font-semibold tabular-nums text-[color:var(--brand-orange)]">{fmt0(a.count)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-[color:var(--text-secondary)] font-medium mb-2">
            <Cloud className="h-3.5 w-3.5" /> Cloudflare R2
          </div>
          <div className="text-2xl font-bold tabular-nums" data-testid="app-stat-r2-bytes">
            {data?.r2?.configured ? fmtBytes(data.r2.bytes) : 'non configurato'}
          </div>
          <div className="text-[11px] text-[color:var(--text-secondary)] mb-2">
            {data?.r2?.configured ? `${fmt0(data.r2.objects)} oggetti · ${fmt0(data.r2.photos)} foto · ${fmt0(data.r2.signatures)} firme` : ''}
          </div>
          {data?.r2?.configured && (
            <>
              <div className="h-1.5 w-full rounded-full bg-zinc-200 overflow-hidden mt-2">
                <div
                  className={`h-full ${r2Pct >= 90 ? 'bg-rose-600' : r2Pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500'} transition-[width] duration-500`}
                  style={{ width: `${r2Pct}%` }}
                />
              </div>
              <div className="text-[11px] text-[color:var(--text-secondary)] mt-1">
                {r2Pct.toFixed(1)}% di 10 GB tier gratuito
              </div>
            </>
          )}
        </div>
      </div>

      {/* MongoDB */}
      {data?.mongo && (
        <div className="mt-4 pt-3 border-t border-[color:var(--hairline)] flex items-center gap-4 flex-wrap text-xs text-[color:var(--text-secondary)]">
          <span className="inline-flex items-center gap-1.5">
            <Database className="h-3.5 w-3.5" /> MongoDB:
          </span>
          <span><strong>{fmt0(data.mongo.raccomandate)}</strong> raccomandate</span>
          <span><strong>{fmt0(data.mongo.tracking_events)}</strong> eventi tracking</span>
          <span><strong>{fmt0(data.mongo.couriers)}</strong> corrieri attivi</span>
        </div>
      )}
    </Card>
  );
}
